import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post, patch } from "../client";
import type { Authority, AuthorityType } from "../types";

const AUTHORITIES_KEY = "authorities";

interface AuthoritySearchParams {
  q?: string;
  type?: AuthorityType;
  limit?: number;
}

/**
 * Search authorities by name.
 */
export function useAuthoritySearch(params: AuthoritySearchParams) {
  const queryParams = new URLSearchParams();

  if (params.q) queryParams.set("q", params.q);
  if (params.type) queryParams.set("type", params.type);
  if (params.limit) queryParams.set("limit", String(params.limit));

  const queryString = queryParams.toString();

  return useQuery({
    queryKey: [AUTHORITIES_KEY, "search", params],
    queryFn: async () => {
      const url = queryString ? `/catalog/authorities?${queryString}` : "/catalog/authorities";
      return get<Authority[]>(url);
    },
    enabled: !!params.q && params.q.length >= 2,
  });
}

/**
 * Get a single authority by ID.
 */
export function useAuthority(id: string | undefined) {
  return useQuery({
    queryKey: [AUTHORITIES_KEY, id],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");
      return get<Authority>(`/catalog/authorities/${id}`);
    },
    enabled: !!id,
  });
}

/**
 * Create a new authority.
 */
export function useCreateAuthority() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      type: AuthorityType;
      preferredName: string;
      variantNames?: string[];
      birthDate?: string;
      deathDate?: string;
    }) => {
      return post<Authority>("/catalog/authorities", input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [AUTHORITIES_KEY] });
    },
  });
}

/**
 * Update an existing authority.
 */
export function useUpdateAuthority(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: Partial<{
        preferredName: string;
        variantNames: string[];
        birthDate: string;
        deathDate: string;
      }>
    ) => {
      return patch<Authority>(`/catalog/authorities/${id}`, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [AUTHORITIES_KEY] });
    },
  });
}
